/**
 * 워크스페이스 선택 — URL(?ws) 과 마지막 선택 영속의 정합(ProjectPickerPage 에서 분리, P4-c).
 *
 * 선택값은 'all' 또는 워크스페이스 id(app/workspaceFilter). 기본 워크스페이스는 행(is_default)이라 그 id 는 목록을 받은 뒤에만
 * 안다 — 그래서 선택은 목록이 올 때까지 **보류**(pending)이고, 화면은 그동안 전환 중과 같은 진행 표시를 보인다(빈 목록이나
 * «프로젝트가 없습니다» 를 거짓으로 그리지 않는다). 무효한 선택(지운 워크스페이스, 옛 예약어 'default' 주소·저장값)은 기본 행으로 정정한다.
 * 목록 조회가 실패하면 그것도 상태(loadFailed)다 — 보류가 영영 안 풀리는 대신 페이지가 오류 카드와 재조회를 보인다. 실패 여부는
 * **그 호출**이 돌려준 결과로만 판정한다 — 스토어의 error 는 이름 변경·생성 실패나 겹친 조회의 늦은 실패가 덮어쓴다.
 */
import { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspaceStore } from '@/app/stores/workspaceStore';
import { useAuthStore } from '@/app/stores/authStore';
import { WORKSPACE_ALL, defaultWorkspaceOf } from '@/app/workspaceFilter';
import { getLastWorkspace, setLastWorkspace } from '@/app/persistence/lastWorkspace';

export function useWorkspaceSelection() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspaces, fetchWorkspaces } = useWorkspaceStore();
  const { user: authUser } = useAuthStore();
  const storedLast = useMemo(() => getLastWorkspace(authUser?.id), [authUser?.id]);
  const requested = searchParams.get('ws') ?? storedLast;
  const defaultWorkspaceId = useMemo(() => defaultWorkspaceOf(workspaces)?.id ?? null, [workspaces]);
  // 전환은 React 전환(transition)으로: 새 목록(카드 수십 장, 측정 빌드 실측 9장에 1.3초)을 그리는 동안 옛 목록이 그대로
  // 남고 switching 이 true 라 페이지가 진행 표시를 보일 수 있다 — 테스터가 본 "4~8초 동안 이전 화면이 그대로,
  // 로딩 표시 없음"(게시판 #34)의 표시 쪽 대응. 시간 자체는 카드 마운트 단가(C)의 몫이다.
  const [switching, startSwitch] = useTransition();
  const selectWorkspace = useCallback((selection: string) => {
    setLastWorkspace(authUser?.id, selection);
    startSwitch(() => {
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('ws', selection); return next; }, { replace: true });
    });
  }, [setSearchParams, authUser?.id]);
  const [workspacesLoaded, setWorkspacesLoaded] = useState(false);
  /** 마지막 목록 조회의 실패 사유 — 없으면 null. 재조회가 풀거나 다시 잡는다 */
  const [loadFailed, setLoadFailed] = useState<string | null>(null);
  const reloadWorkspaces = useCallback(async () => {
    const result = await fetchWorkspaces();
    setLoadFailed(result.ok ? null : result.error);
    setWorkspacesLoaded(true);
  }, [fetchWorkspaces]);
  useEffect(() => { void reloadWorkspaces(); }, [reloadWorkspaces]);
  // 요청된 선택이 유효한가 — 'all' 또는 목록에 있는 id. 목록을 받기 전엔 판정하지 않는다
  const requestedValid = workspacesLoaded && requested !== null && (requested === WORKSPACE_ALL || workspaces.some((w) => w.id === requested));
  // ?ws 가 없으면 저장된 마지막 선택(유효할 때) → 없으면 기본 행. 무효한 선택(지운 id·옛 'default')도 기본 행으로 정정
  useEffect(() => {
    if (!workspacesLoaded) return;
    if (requested !== null && requestedValid) {
      if (!searchParams.get('ws')) selectWorkspace(requested); // 저장값을 URL 에 반영
      return;
    }
    if (defaultWorkspaceId) selectWorkspace(defaultWorkspaceId);
  }, [workspacesLoaded, requested, requestedValid, defaultWorkspaceId, searchParams, selectWorkspace]);
  /** 확정된 선택 — 보류 중이면 null */
  const wsSelection = requestedValid ? requested : null;
  const pending = wsSelection === null;
  // 제목은 선택 상태를 그대로 말한다 — 'all' 이 '기본 워크스페이스' 로 보이던 오표기(게시판 #21)
  const headerTitle = useMemo(() => {
    if (wsSelection === null) return '';
    if (wsSelection === WORKSPACE_ALL) return '전체 워크스페이스';
    return workspaces.find((w) => w.id === wsSelection)?.name ?? '';
  }, [wsSelection, workspaces]);
  return { switching, pending, wsSelection, selectWorkspace, workspaces, defaultWorkspaceId, headerTitle, loadFailed, reloadWorkspaces };
}
